import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { UiButtonDirective } from '@template-ui/directives';
import { ICellRendererAngularComp, IHeaderAngularComp } from 'ag-grid-angular';
import { ICellRendererParams, IHeaderParams } from 'ag-grid-community';
import { WorkflowDefinitionLifecycleDetail } from './platform-workflow-definition-lifecycle.models';

type WorkflowIdentityCellParams =
  ICellRendererParams<WorkflowDefinitionLifecycleDetail> & {
    openDefinition: (definition: WorkflowDefinitionLifecycleDetail) => void;
  };

/** Angular grid integration keeps registry actions on the shared control API. */
@Component({
  selector: 'phms-workflow-catalog-identity-cell',
  imports: [CommonModule, UiButtonDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './workflow-catalog-identity-cell.component.html',
  styles: [
    ':host { display: flex; align-items: center; gap: .5rem; min-width: 0; max-width: 100%; width: 100%; } button { flex: 1 1 auto; min-width: 0; max-width: 100%; justify-content: flex-start; } span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; } :host > span { flex: 0 1 auto; max-width: 45%; }',
  ],
})
export class WorkflowCatalogIdentityCellComponent implements ICellRendererAngularComp {
  definition: WorkflowDefinitionLifecycleDetail | undefined;
  private openDefinition?: WorkflowIdentityCellParams['openDefinition'];
  private readonly changeDetector = inject(ChangeDetectorRef);

  get title(): string {
    return (
      this.definition?.displayName?.trim() ||
      this.definition?.workflowCode ||
      ''
    );
  }

  agInit(params: WorkflowIdentityCellParams): void {
    this.definition = params.data;
    this.openDefinition = params.openDefinition;
    this.changeDetector.markForCheck();
  }

  refresh(params: WorkflowIdentityCellParams): boolean {
    this.agInit(params);
    return true;
  }

  open(): void {
    if (this.definition) this.openDefinition?.(this.definition);
  }
}

/** Native keyboard activation and sort indicators for the installed grid header API. */
@Component({
  selector: 'phms-workflow-catalog-sort-label',
  imports: [UiButtonDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './workflow-catalog-sort-label.component.html',
  styles: [
    ':host { display: block; min-width: 0; } button { min-width: 0; max-width: 100%; }',
  ],
})
export class WorkflowCatalogSortLabelComponent
  implements IHeaderAngularComp, OnDestroy
{
  label = '';
  sortable = true;
  sortDirection: 'asc' | 'desc' | null = null;
  private params?: IHeaderParams;
  private readonly changeDetector = inject(ChangeDetectorRef);
  private readonly onSortChanged = () => {
    this.sortDirection = this.params?.column.getSort() ?? null;
    this.changeDetector.markForCheck();
  };
  agInit(params: IHeaderParams): void {
    this.ngOnDestroy();
    this.params = params;
    this.label = params.displayName;
    this.sortable = params.enableSorting !== false;
    params.column.addEventListener('sortChanged', this.onSortChanged);
    this.onSortChanged();
  }
  refresh(params: IHeaderParams): boolean {
    this.agInit(params);
    return true;
  }

  sort(event: MouseEvent): void {
    if (this.sortable) this.params?.progressSort(event.shiftKey);
  }

  ngOnDestroy(): void {
    this.params?.column.removeEventListener('sortChanged', this.onSortChanged);
  }
}
